import { Component, OnInit, OnDestroy, Input, SimpleChanges } from '@angular/core';
import { KilnService } from '../services/kiln.service';
import { HealthDiagnosticsService, HealthDiagnosticsResponse } from '../services/health-diagnostics.service';
import { PopoverController } from '@ionic/angular';
import { InfoPopoverComponent } from './info-popover.component';
import { HealthState } from './kiln-health-visual.component';

@Component({
  selector: 'app-health',
  templateUrl: './health.component.html',
  styleUrls: ['./health.component.scss']
})
export class HealthComponent implements OnInit {
  @Input() kilnId!: string;
  @Input() mac: string;
  @Input() diag;

  loading: boolean = true;
  error: string | null = null;
  kilnData: any = null;
  healthData: HealthDiagnosticsResponse | null = null;

  elements = [];
  zoneStates: HealthState[] = ['good', 'good', 'good'];
  controllerState: HealthState = 'good';
  outletState: HealthState = 'good';
  clockState: HealthState = 'good';

  // Diagnostics object with factory specs and current values
  diagnostics = {
    // Voltages and Amperages
    v1: { title: "Voltage 1", value: 238, label: "V", factorySpec: 240 },
    v2: { title: "Voltage 2", value: 237, label: "V", factorySpec: 240 },
    v3: { title: "Voltage 3", value: 237, label: "V", factorySpec: 240 },
    a1: { title: "Amperage 1", value: 45, label: "A", factorySpec: 48 },
    a2: { title: "Amperage 2", value: 44, label: "A", factorySpec: 48 },
    a3: { title: "Amperage 3", value: 43, label: "A", factorySpec: 48 },
    // No Load and Full Load Voltages
    nl: { title: "No Load", value: 240, label: "V", factorySpec: 240 },
    fl: { title: "Full Load", value: 235, label: "V", factorySpec: 235 },
    board_t: { title: "Board Temperature", value: 120, label: "F" },
    last_err: { title: "Last Error", value: "None", label: "" },
    date: { title: "Last Updated", value: new Date().toLocaleString(), label: "" },

    // Diagnostic checks (booleans and calculations)
    controllerOutputsOk: false,
    buildingVoltageOk: false,
    wireSizeOk: false,
    allZonesEnergizing: false,
    wattageDrop: '',
    maxTempEstimate: '',
    nonEnergizedZones: '',
    lowPowerOutputs: ''
  };

  private previousKilnId: string | undefined;
  private previousMac: string | undefined;

  constructor(
    private kilnService: KilnService,
    private healthDiagnosticsService: HealthDiagnosticsService,
    private popoverController: PopoverController
  ) {}

  // Check if all elements are heating
  get allElementsHeating(): boolean {
    return this.elements.every(e => e.isHeating);
  }

  /** Wattage string from API; hide invalid numbers (e.g. NaN%). */
  get wattageDropDisplay(): string {
    const w = this.diagnostics?.wattageDrop;
    if (w == null || w === '') {
      return 'N/A';
    }
    const s = String(w);
    return /NaN/i.test(s) ? 'N/A' : s;
  }

  ngOnInit() {
    this.loading = true;
    if (this.kilnId && this.mac) {
      this.initializeHealthData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const kilnIdChanged = changes['kilnId'] && changes['kilnId'].currentValue !== this.previousKilnId;
    const macChanged = changes['mac'] && changes['mac'].currentValue !== this.previousMac;

    if ((kilnIdChanged || macChanged) && this.kilnId && this.mac) {
      this.initializeHealthData();
      this.previousKilnId = this.kilnId;
      this.previousMac = this.mac;
    }
  }

  private initializeHealthData() {
    if (this.kilnId && this.mac) {
      this.loading = true;
      this.error = null;
      
      this.healthDiagnosticsService.getHealthDiagnostics(this.kilnId, this.mac)
        .subscribe({
          next: (response) => {
            // console.log(`response: ${JSON.stringify(response)}`);
            this.healthData = response;
            this.elements = response.elements;
            
            // Merge diagnostic data and health checks into the diagnostics object
            this.diagnostics = {
              ...response.diagnosticData,
              ...response.healthChecks
            };
            this.updateVisualStates();
            
            this.loading = false;
          },
          error: (error) => {
            console.error('Error fetching health diagnostics:', error);
            this.error = "Error retrieving health diagnostics data";
            this.loading = false;
          }
        });
    } else {
      console.error('No kiln ID / MAC provided');
      this.error = "Missing kiln serial number or MAC";
      this.loading = false;
    }
  }

  async presentInfoPopover(ev: Event, header: string, text: string) {
    const popover = await this.popoverController.create({
      component: InfoPopoverComponent,
      event: ev,
      translucent: true,
      cssClass: 'info-popover',
      componentProps: {
        header,
        text
      }
    });
    await popover.present();
  }

  presentWireSizeInfo(ev: Event) {
    this.presentInfoPopover(
      ev,
      'Wire Size Adequacy',
      `This measurement compares the voltage drop between No Load and Full Load conditions.
      A large voltage drop may indicate that the wire size is too small for the kiln's power requirements,
      which can cause inefficient operation or safety issues.`
    );
  }

  presentAllZonesInfo(ev: Event) {
    this.presentInfoPopover(
      ev,
      'All Zones Energizing',
      `This check confirms that all heating zones in the kiln are receiving power and are active by checking the amperages from the current sensor. 
      If one or more zones are not energizing, it may indicate a wiring or relay issue, which can lead to uneven heating and poor firing results.`
    );
  }

  presentControllerOutputsInfo(ev: Event) {
    this.presentInfoPopover(
      ev,
      'Controller Outputs Powering',
      `This check verifies that the kiln controller is sending the correct signals for each output. 
      If the controller outputs are not functioning, the kiln may not heat properly or at all.`
    );
  }

  private updateVisualStates() {
    const nonEnergizedZones = this.parseZoneList(this.diagnostics.nonEnergizedZones);
    const lowPowerZones = this.parseZoneList(this.diagnostics.lowPowerOutputs);

    const a1State = this.amperageToState(this.diagnostics?.a1?.value, this.diagnostics?.a1?.factorySpec);
    const a2State = this.amperageToState(this.diagnostics?.a2?.value, this.diagnostics?.a2?.factorySpec);
    const a3State = this.amperageToState(this.diagnostics?.a3?.value, this.diagnostics?.a3?.factorySpec);

    this.zoneStates = [a1State, a2State, a3State].map((state, index) => {
      const zoneIndex = index + 1;
      if (nonEnergizedZones.includes(zoneIndex)) {
        return 'critical';
      }
      if (lowPowerZones.includes(zoneIndex)) {
        return state === 'good' ? 'warning' : state;
      }
      return state;
    });

    this.controllerState = this.diagnostics.controllerOutputsOk ? 'good' : 'critical';
    this.clockState = this.diagnostics.allZonesEnergizing ? 'good' : 'warning';
    this.outletState = this.diagnostics.wireSizeOk ? 'good' : 'warning';
  }

  private amperageToState(value: number, factorySpec: number): HealthState {
    if (!factorySpec || value == null) {
      return 'warning';
    }
    const ratio = value / factorySpec;
    if (ratio >= 0.9) {
      return 'good';
    }
    if (ratio >= 0.75) {
      return 'warning';
    }
    return 'critical';
  }

  private parseZoneList(rawZones: string): number[] {
    if (!rawZones) {
      return [];
    }
    const matches = rawZones.match(/\d+/g);
    if (!matches) {
      return [];
    }
    return matches
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0 && value <= 3);
  }
}
